import { Component } from '@theme/component';

/**
 * @typedef {Object} IngredientsGridRefs
 * @property {HTMLButtonElement} [toggleButton] - The expand/collapse toggle button (external, found in parent section)
 * @property {HTMLElement} [toggleText] - The text span inside toggle button
 */

/**
 * Ingredients grid component that handles expand/collapse functionality.
 * The toggle button is external to this component (in the parent section),
 * so we find it via closest() rather than refs.
 *
 * @extends {Component<IngredientsGridRefs>}
 */
class NcnIngredientsGrid extends Component {
  /** @type {AbortController | null} */
  #abortController = null;

  /** @type {HTMLButtonElement | null} */
  #toggleButton = null;

  /** @type {HTMLElement | null} */
  #toggleText = null;

  /** @type {boolean} */
  #isExpanded = false;

  connectedCallback() {
    super.connectedCallback();

    this.#abortController = new AbortController();
    const { signal } = this.#abortController;

    // Find toggle button in parent section
    // Note: Button is external to this component, so we use closest() + querySelector
    // The ref pattern would require the button to be inside this component's DOM
    const section = this.closest('.ncn-ingredients');
    if (!section) {
      console.warn('NcnIngredientsGrid: Parent .ncn-ingredients section not found');
      return;
    }

    this.#toggleButton = /** @type {HTMLButtonElement | null} */ (
      section.querySelector('[data-ingredients-toggle]')
    );
    this.#toggleText = /** @type {HTMLElement | null} */ (
      this.#toggleButton?.querySelector('[data-toggle-text]') ?? null
    );

    if (this.#toggleButton) {
      this.#toggleButton.addEventListener('click', this.#handleToggle, { signal });
    }
  }

  disconnectedCallback() {
    this.#abortController?.abort();
    this.#abortController = null;
    this.#toggleButton = null;
    this.#toggleText = null;
    super.disconnectedCallback();
  }

  /**
   * Toggles the expanded state of the grid
   */
  #handleToggle = () => {
    try {
      this.#isExpanded = !this.#isExpanded;

      // Update grid class
      this.classList.toggle('ncn-ingredients-grid--expanded', this.#isExpanded);

      // Update button state
      if (this.#toggleButton) {
        this.#toggleButton.setAttribute('aria-expanded', String(this.#isExpanded));

        // Update button text using cached reference
        if (this.#toggleText) {
          const newText = this.#isExpanded
            ? this.#toggleText.dataset.hideText
            : this.#toggleText.dataset.showText;
          this.#toggleText.textContent = newText || '';
        }
      }

      // Scroll to first newly visible item if expanding
      if (this.#isExpanded) {
        // Visibility is controlled via nth-child CSS rules, so compute
        // the first previously-hidden item by index from data attributes.
        const isMobile = window.matchMedia('(max-width: 749px)').matches;
        const countAttr = isMobile ? 'mobileVisibleCount' : 'visibleCount';
        const visibleCount = parseInt(this.dataset[countAttr] ?? '0', 10);
        const items = this.querySelectorAll('.ncn-ingredients__item');
        const firstNewItem = visibleCount > 0 ? items[visibleCount] : null;
        if (firstNewItem) {
          const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
          requestAnimationFrame(() => {
            firstNewItem.scrollIntoView({
              behavior: prefersReducedMotion ? 'auto' : 'smooth',
              block: 'nearest',
            });
          });
        }
      }
    } catch (error) {
      console.error('NcnIngredientsGrid: Failed to toggle grid', error);
    }
  };
}

if (!customElements.get('ncn-ingredients-grid')) {
  customElements.define('ncn-ingredients-grid', NcnIngredientsGrid);
}
